import React from 'react';
import Plus from "../../../../picture/plus-resale.svg";
import Minus from "../../../../picture/minus-resale.svg";
import Lock from "../../../../picture/lock.svg";
import { Checkbox } from 'semantic-ui-react'
import { useTranslation } from "react-i18next";
import { Button } from '../../../ui';
import { APIAction } from '../../../order-menu/api-action/api-action';
import Message from './message';
import formattedAmount from './utils';
import './style/resaleBreakdown.css';

function LineItem({ text, amount, disabled, info, className = '' }) {
    return (
        <div className={`wz-list-item ${disabled && 'wz-list-item-disabled'} ${info && 'wz-list-item-info'}`}>
            <div className='wz-list-item-title'>{text}</div>
            <div className={`wz-list-item-amount ${className}`}>{amount}</div>
        </div>
    )
}

export default function ResaleBreakdown({ data, state, setState, mode, onSubmit, handleSubmit = () => { }, setEditResale }) {
    const { t } = useTranslation();
    const [showMore, setShowMore] = React.useState(true);
    const [confirmationStep, setConfirmationStep] = React.useState(false);

    const price = data.resale_breakdown.price
    const minPrice = price.min;
    const maxPrice = price.max;
    const fees = data.resale_breakdown.fees;
    const total_price = data.resale_breakdown.total_price;
    const resaleOption = data.resale_option;
    const message = data.message && Object.keys(data.message).reduce((messageObject, key) => {
        if (!data.message[key].includes('widget_template.manage_order.resale.'))
            messageObject[key] = data.message[key];
        return messageObject;
    }, {});

    const inputError = React.useMemo(() => {
        return state.price && (state.price < minPrice || state.price > maxPrice);
    }, [state, minPrice, maxPrice]);

    const total = React.useMemo(() => {
        const value = state.price - fees.value;
        return value >= 0 ? value : 0
    }, [state, fees]);

    const toggleShowMore = React.useCallback(() => {
        setShowMore(showMore => !showMore);
    }, [showMore]);

    const cancelResale = React.useCallback(() => {
        onSubmit({
            data: {
                action: 'DeleteTicketResaleStep'
            }
        })
    }, [onSubmit]);

    const editResale = React.useCallback(() => {
        onSubmit({
            data: {
                action: 'UpdateTicketResaleStep'
            }
        })
    }, [onSubmit]);

    return (
        <div className='wz-breakdown-container'>
            <div className='wz-breakdown-title'>{t('widget.resale.breakdown.title')}</div>
            {
                mode === 'resale-menu' ? (
                    <div>
                        <div>
                            <LineItem text={price.label} amount={formattedAmount(state.price || 0, price.currency)} />
                            {fees.value > 0 && <LineItem text={fees.label} amount={formattedAmount(fees.value, fees.currency)} disabled /> }
                            <LineItem className='wz-list-item-total' text={total_price.label} amount={formattedAmount(total, total_price.currency)} info />
                        </div>
                        <div className='wz-breakdown-cta'>
                            <APIAction name={'UpdateTicketResaleStep'} actions={data.actions}>
                                <Button
                                    className='wz-button wz-cancel-cta'
                                    label={t('widget.resale.breakdown.cta-modify')}
                                    onClick={editResale}
                                />
                            </APIAction>
                            <APIAction name={'DeleteTicketResaleStep'} actions={data.actions}>
                                <Button
                                    className='wz-edit-cta'
                                    label={t('widget.resale.breakdown.cta-cancel')}
                                    onClick={cancelResale}
                                />
                            </APIAction>
                        </div>
                    </div>
                ) :
                    confirmationStep ? (
                        <div>
                            {message && (
                                <div>
                                    <Message
                                        type={message?.type ? message.type : 'info'}
                                        title={message?.title ? message.title : t('widget.resale.breakdown.message-info-title')}
                                        description={message?.body ? message.body : t('widget.resale.breakdown.message-info-description')}
                                    />
                                </div>)}
                            <div>
                                <LineItem text={price.label} amount={formattedAmount(state.price || 0, price.currency)} />
                                {fees.value ? <LineItem text={fees.label} amount={formattedAmount(fees.value, fees.currency)} disabled /> : null}
                                <LineItem className='wz-list-item-total' text={total_price.label} amount={formattedAmount(total, total_price.currency)} info />
                            </div>
                            <div className='wz-breakdown-cta'>
                                <Button
                                    className='wz-button'
                                    label={t('widget.resale.breakdown.cta-confirm')}
                                    onClick={handleSubmit}
                                />
                                <Button
                                    className='wz-button wz-back-cta'
                                    label={t('widget.resale.breakdown.cta-back')}
                                    onClick={() => setConfirmationStep(false)}
                                />
                            </div>
                        </div>
                    )
                        :
                        (<>
                            <div className='wz-breakdown-content'>
                                <div className='wz-breakdown-optional'>
                                    <div className='wz-breakdown-left-content'>
                                        <div className='wz-breakdown-icon' onClick={toggleShowMore}>
                                            <img alt='' src={showMore ? Minus : Plus} />
                                        </div>
                                        <div>
                                            <div>{price.label}</div>
                                            {price.min !== price.max && <div className='wz-detail-description'>{price.description}</div>}
                                        </div>
                                    </div>
                                    <div>
                                        <div className='wz-price-input-container'>
                                            <input
                                                placeholder={data.header.paid_price}
                                                type='number'
                                                disabled={minPrice === maxPrice}
                                                min={minPrice}
                                                value={state.price}
                                                onChange={({ target: { value } }) => setState({ ...state, 'price': value })}
                                                className={`wz-price-input ${inputError && 'wz-price-input-error'}`}
                                            />
                                            <div className='wz-price-icon'>
                                                {price.currency}
                                            </div>
                                        </div>
                                        {
                                            inputError && <div className='wz-input-error'>{t('widget.resale.breakdown.input-error', { min: formattedAmount(minPrice, price.currency), max: formattedAmount(maxPrice, price.currency) })}</div>
                                        }
                                    </div>
                                </div>
                                {showMore &&
                                    <div>
                                        <LineItem text={price.label} amount={formattedAmount(state.price || 0, price.currency)} />
                                        {fees.value ? <LineItem text={fees.label} amount={formattedAmount(fees.value, fees.currency)} disabled /> : null}
                                    </div>
                                }
                            </div>
                            <LineItem className='wz-list-item-total' text={total_price.label} amount={formattedAmount(total, total_price.currency)} info />
                            {resaleOption.allow_private_resale &&
                                <>
                                    <div className='wz-breakdown-title'>{t('widget.resale.breakdown.resale_options')}</div>
                                    <div className='wz-breakdown-content'>
                                        <div className='wz-breakdown-toggle'>
                                            <div className='wz-breakdown-left-content'>
                                                <div className='wz-breakdown-icon' onClick={toggleShowMore}>
                                                    <img alt='' src={Lock} />
                                                </div>
                                                <div>
                                                    <div className='wz-detail-label'>{resaleOption.label}</div>
                                                    <div className='wz-detail-description'>{resaleOption.description}</div>
                                                </div>
                                            </div>
                                            <div className='wz-price-input-container'>
                                                <Checkbox toggle checked={state.is_private_resale} onChange={(_, { checked }) => setState({ ...state, 'is_private_resale': checked })} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                            <div className='wz-breakdown-cta'>
                                <Button
                                    className='wz-button'
                                    label={mode === 'create' ? t('widget.resale.breakdown.cta-create') : t('widget.resale.breakdown.cta-edit')}
                                    onClick={() => setConfirmationStep(true)}
                                    disabled={!state.price || inputError}
                                />
                            </div>
                        </>)
            }
        </div>
    )
}
